import { create } from 'zustand';
import type { AppConfig } from '@/types/auth';
import { parseAppCode } from '@/lib/utils/url';
import { SAAS_ALIASES, TPA_ALIASES } from '@/config/constants';

interface AppStore {
  extensionName: string | null;
  appCodeTPA: string | null;
  appCodeSAAS: string | null;
  appConfig: AppConfig | null;
  initialized: boolean;
  error: string | null;

  parseAndSetAppCode(appCode: string): boolean;
  setConfig(config: AppConfig): void;
  setError(error: string | null): void;
  reset(): void;
}

export const useAppStore = create<AppStore>((set) => ({
  extensionName: null,
  appCodeTPA: null,
  appCodeSAAS: null,
  appConfig: null,
  initialized: false,
  error: null,

  parseAndSetAppCode: (appCode: string) => {
    const parsed = parseAppCode(appCode);
    if (!parsed) {
      set({ error: `Invalid appCode: ${appCode}` });
      return false;
    }

    // Resolve aliases (e.g. "kudosity" → "burstsms", "salesporcecrm" → "salesforce")
    const resolvedTpa = TPA_ALIASES[parsed.tpa] || parsed.tpa;
    const resolvedSaas = SAAS_ALIASES[parsed.saas] || parsed.saas;
    // Use original (unresolved) saas code in extension name for Firestore paths.
    // "salesporcecrm" must NOT become "salesforce" because the resulting
    // "twilioforsalesforce" contains two "for" separators and breaks re-parsing.
    const resolvedExtensionName = `${resolvedTpa}for${parsed.saas}`;

    set({
      extensionName: resolvedExtensionName,
      appCodeTPA: resolvedTpa,
      appCodeSAAS: resolvedSaas,
      initialized: true,
      error: null,
    });
    return true;
  },

  setConfig: (config) =>
    set({
      appConfig: config,
      extensionName: config.extensionName,
      appCodeTPA: config.appCodeTPA,
      appCodeSAAS: config.appCodeSAAS,
      initialized: true,
    }),

  setError: (error) => set({ error }),

  reset: () =>
    set({
      extensionName: null,
      appCodeTPA: null,
      appCodeSAAS: null,
      appConfig: null,
      initialized: false,
      error: null,
    }),
}));
